

import type { OptionOnSelectData} from "@fluentui/react-components";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface,  Dropdown, Field, Option, makeStyles, tokens } from "@fluentui/react-components";
import { useState } from "react";
import type { IConfigurationEntity, SettingsData, SettingValueType } from "../../../interfaces";
import { ValueInput } from "../ValueInput/ValueInput";

export interface IAddDialogProps {
    configurationEntities: IConfigurationEntity[];
    onCancel(): void;
    onAdd(data: SettingsData): void;
}

const useStyles = makeStyles({
    optionDiv: {
        display: 'flex',
        flexDirection: 'column'
    },
    optionDescription: {
        fontStyle: 'italic',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
    }
});

export const AddDialog = (props: IAddDialogProps) => {
    const { configurationEntities, onAdd, onCancel,  } = props;
    const styles = useStyles();

    const [selectedEntity, setSelectedEntity] = useState<IConfigurationEntity | null>(null);
    const [value, setValue] = useState<SettingValueType>("");
    const [disableApplyButton, setDisableApplyButton] = useState(true);

    const onAdded = (): void => {
        if (selectedEntity) {
            const newSetting: SettingsData = { [selectedEntity.key]: value };
            onAdd(newSetting);
        }
    };

    const onKeySelected = (data: OptionOnSelectData) => {
        const newKey = String(data.optionValue);
        const entity = configurationEntities.find(x => x.key === newKey);
        setSelectedEntity(entity ?? null);
    
        if (entity?.type === "boolean") {
            setValue(entity.defaultValue === true);
            setDisableApplyButton(false);
        } else {
            const defaultValue = entity?.defaultValue ?? "";
            setValue(defaultValue);
            setDisableApplyButton(defaultValue === "");
        }
    };

    const onIsValidChanged = (isValid: boolean) => {
        const disableApply = !isValid || selectedEntity === null;
        setDisableApplyButton(disableApply);
    };

    const onValueChanged = (newValue: SettingValueType): void => {
        setValue(newValue);
    };

    const renderInputControl = () => {
        return (
            <>
                {selectedEntity &&
                    <ValueInput
                        label={selectedEntity.shortDescription ?? ""}
                        value={value}
                        entity={selectedEntity}
                        onIsValidChange={onIsValidChanged}
                        onValueChange={onValueChanged} />}

            </>
        );
    };

    return (
        <Dialog open>
            <DialogSurface>
                <DialogBody>
                    <DialogContent>
                        <Field label="Name" required>
                            <Dropdown aria-label="Select a setting" placeholder="Select a setting" {...props} onOptionSelect={(_, data) => onKeySelected(data)}>
                                {configurationEntities.map((option) => (
                                    <Option key={option.key} text={option.name ?? option.key} value={option.key} >
                                        <div className={styles.optionDiv}>
                                            <span>{option.name}</span>
                                            <span className={styles.optionDescription}>{option.shortDescription}</span>
                                        </div>
                                    </Option>
                                ))}
                            </Dropdown>
                        </Field>
                        <div>{renderInputControl()}</div>
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="primary" onClick={() => onAdded()} disabled={disableApplyButton}>Apply</Button>
                        <Button appearance="secondary" onClick={() => onCancel()}>Cancel</Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};